import React, { useState, useEffect, useMemo } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import restaurantAPI from '../../../api/restaurantAPI';

const BILLS = [1000, 500, 200, 100, 50, 20, 10, 5, 1];

function fmt(centavos) {
  return `₱${((centavos || 0) / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function CloseShiftModal({ isOpen, shift, onClose, onShiftClosed }) {
  const [counts, setCounts] = useState(() => Object.fromEntries(BILLS.map(b => [b, 0])));
  const [closingNotes, setClosingNotes] = useState('');
  const [summary, setSummary] = useState(null);
  const [loadingSum, setLoadingSum] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(false);

  // Load sales summary when modal opens
  useEffect(() => {
    if (!isOpen || !shift?._id) return;
    let cancelled = false;
    setLoadingSum(true);
    setError(null);
    restaurantAPI.shifts.getSummary(shift._id)
      .then(res => {
        if (!cancelled) setSummary(res.data?.summary || null);
      })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load shift summary');
      })
      .finally(() => { if (!cancelled) setLoadingSum(false); });
    return () => { cancelled = true; };
  }, [isOpen, shift?._id]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCounts(Object.fromEntries(BILLS.map(b => [b, 0])));
      setClosingNotes('');
      setError(null);
      setConfirm(false);
    }
  }, [isOpen]);

  const closingCash = useMemo(
    () => BILLS.reduce((s, b) => s + b * (counts[b] || 0) * 100, 0),
    [counts]
  );

  const expectedCash = useMemo(
    () => (shift?.openingFloat || 0) + (summary?.cashSales || 0),
    [shift, summary]
  );

  const overShort = closingCash - expectedCash;

  function handleCount(bill, val) {
    const n = Math.max(0, parseInt(val, 10) || 0);
    setCounts(prev => ({ ...prev, [bill]: n }));
  }

  async function handleClose() {
    setError(null);
    setSubmitting(true);
    try {
      const denominations = BILLS.map(b => ({ bill: b, count: counts[b] || 0 })).filter(d => d.count > 0);
      if (!denominations.length) {
        setError('Enter at least one denomination count before closing.');
        return;
      }
      await restaurantAPI.shifts.closeWithDenominations(shift._id, denominations, closingNotes);
      onShiftClosed();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to close shift');
      setConfirm(false);
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePrintReport() {
    try {
      const denominations = BILLS.map(b => ({ bill: b, count: counts[b] || 0 }));
      await restaurantAPI.shifts.printReport(shift._id, denominations);
    } catch (err) {
      setError(err.response?.data?.message || 'Print failed');
    }
  }

  if (!isOpen) return null;

  const startedAt = shift?.startedAt ? new Date(shift.startedAt) : null;
  const duration  = startedAt ? formatDistanceToNow(startedAt, { addSuffix: false }) : '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-[620px] max-h-[90vh] flex flex-col mx-4">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Close Shift</h2>
          <div className="mt-1 text-sm text-gray-600 flex gap-6">
            <span>Cashier: <strong>{shift?.cashierName || '—'}</strong></span>
            <span>Terminal: <strong>{shift?.terminalId || '—'}</strong></span>
            {startedAt && (
              <span>Started: <strong>{format(startedAt, 'h:mm a')}</strong> ({duration} ago)</span>
            )}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">

          {/* Section A — Denomination Declaration */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Declare Closing Cash
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs border-b border-gray-200">
                  <th className="text-left py-1 font-medium">Bill / Coin</th>
                  <th className="text-center py-1 font-medium w-24">Count</th>
                  <th className="text-right py-1 font-medium w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {BILLS.map(bill => {
                  const amt = bill * (counts[bill] || 0) * 100;
                  return (
                    <tr key={bill} className="border-b border-gray-100">
                      <td className="py-1 text-gray-800 font-medium">₱{bill.toLocaleString()}</td>
                      <td className="py-1 text-center">
                        <input
                          type="number"
                          min="0"
                          value={counts[bill] || ''}
                          placeholder="0"
                          onChange={e => handleCount(bill, e.target.value)}
                          className="w-20 text-center border border-gray-300 rounded px-2 py-0.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </td>
                      <td className="py-1 text-right text-gray-700">{fmt(amt)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td colSpan={2} className="py-2 font-bold text-gray-900">Total Declared Cash</td>
                  <td className="py-2 text-right font-bold text-gray-900">{fmt(closingCash)}</td>
                </tr>
              </tfoot>
            </table>
          </section>

          {/* Section B — Balance Summary */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Shift Balance Summary
            </h3>
            {loadingSum ? (
              <p className="text-sm text-gray-500">Loading sales data…</p>
            ) : (
              <div className="space-y-1 text-sm">
                <Row label="Opening Float" value={fmt(shift?.openingFloat || 0)} />
                <Row label="Cash Sales" value={fmt(summary?.cashSales || 0)} />
                <div className="border-t border-gray-200 my-1" />
                <Row label="Expected Cash" value={fmt(expectedCash)} bold />
                <Row label="Declared Cash" value={fmt(closingCash)} bold />
                <OverShortRow value={overShort} />

                <div className="border-t border-gray-200 my-2" />
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Non-Cash (for reference)
                </p>
                {['gcash', 'maya', 'card', 'charge'].map(m => {
                  const amt = summary?.byPaymentMethod?.[m];
                  if (!amt) return null;
                  const label = m === 'charge' ? 'Charge (to AR)' : m.charAt(0).toUpperCase() + m.slice(1);
                  return <Row key={m} label={label} value={fmt(amt)} />;
                })}
                <div className="border-t border-gray-200 my-1" />
                <Row label="Gross Sales" value={fmt(summary?.grossSales || 0)} bold />
                <Row label="Discounts" value={`-${fmt(summary?.totalDiscount || 0)}`} />
                <Row label="Net Sales" value={fmt(summary?.netSales || 0)} bold />
              </div>
            )}
          </section>

          {error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
          )}

          {/* Section C — Notes */}
          <section>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Closing Notes <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={2}
              value={closingNotes}
              onChange={e => setClosingNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter any notes about this shift…"
            />
          </section>

          {/* Confirm step */}
          {confirm && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <p className="font-semibold text-amber-800 mb-3">
                Are you sure? This will end the shift and print the Z reading.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirm(false)}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                >
                  Go Back
                </button>
                <button
                  onClick={handleClose}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold disabled:opacity-50"
                >
                  {submitting ? 'Closing…' : 'Yes, Close Shift'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        {!confirm && (
          <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handlePrintReport}
              disabled={submitting || loadingSum}
              className="px-4 py-2 border border-blue-300 text-blue-700 rounded-lg text-sm hover:bg-blue-50 disabled:opacity-50"
            >
              🖨 Print Report Only
            </button>
            <button
              onClick={() => { setError(null); setConfirm(true); }}
              disabled={submitting || loadingSum}
              className="ml-auto px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
            >
              Close Shift
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function OverShortRow({ value }) {
  if (value === 0) {
    return (
      <div className="flex justify-between font-semibold text-green-700">
        <span>Over / Short</span>
        <span>BALANCED ✓</span>
      </div>
    );
  }
  const isOver = value > 0;
  return (
    <div className={`flex justify-between font-semibold ${isOver ? 'text-amber-700' : 'text-red-700'}`}>
      <span>Over / Short</span>
      <span>{isOver ? `OVER +${fmt(value)}` : `SHORT ${fmt(value)}`}</span>
    </div>
  );
}

export default CloseShiftModal;
